import { useContext, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'clsx';
import { Link } from 'react-router-dom';
import interpolateComponents from 'interpolate-components';
import {
  selectIsExpiredWebSubscriber,
  selectIsUserSubscribed,
} from 'src/common/selectors/me';
import {
  selectExperiment,
  selectIsSubscriptionEnabled,
} from 'src/common/selectors/config';
import subscribe, {
  labels,
} from '../../constants/analytics/categoryActionLabel/subscribe';
import getSubscribeEventLabel from '../../utils/subscription/getSubscribeEventLabel';
import PillButton from '../shared/button/PillButton';
import Checkmark from '../shared/svgIcons/Checkmark';
import { logSubscribeActivity } from '../../actions/logging';
import createSubscribeLink from '../../utils/subscription/createSubscribeLink';
import cssVariables from '../../styles/variables';
import {
  UPSELL_SIDEBAR_HEADLINE,
  UPSELL_START_YOUR_FREE_TRIAL,
  UPSELL_GO_PREMIUM,
  UPSELL_UPGRADE_NOW,
  UPSELL_COMMERCIAL_FREE_NEWS,
  UPSELL_COMMERCIAL_FREE_MUSIC,
  UPSELL_COMMERCIAL_FREE_SPORTS,
  UPSELL_LESS_ADS,
  RIGHTUPSELL_TITLE_DISCOUNT,
} from '../../constants/localizations/upsell';

import {
  LINE_BREAK_INSTRUCTION,
  LINE_BREAK_INTERPOLATE,
} from '../../constants/localizations/shared';

import {
  COMMERCIAL_FREE_MUSIC_ENABLED,
  NEW_RIGHTSIDEBAR_UPSELL_ENABLED,
} from '../../constants/experiments/upsell';
import { LocationAndLocalizationContext } from '../../providers/LocationAndLocalizationProvider';
import css from './upsell.module.scss';

const svgProps = {
  width: '14px',
  height: '12px',
  viewBox: '0 0 14 12',
  fill: cssVariables['--primary-color-1'],
  className: css.checkmark,
};

const dataTestIdPrefix = 'sidebarUpsell';

function UpsellRightSidebar({ guideId, isProfile }) {
  const dispatch = useDispatch();
  const isUserSubscribed = useSelector(selectIsUserSubscribed);
  const isSubscriptionEnabled = useSelector(selectIsSubscriptionEnabled);
  const isExpiredWebSubscriber = useSelector(selectIsExpiredWebSubscriber);
  const isCommercialFreeMusicEnabled = useSelector((state) =>
    selectExperiment(state, COMMERCIAL_FREE_MUSIC_ENABLED),
  );
  const newRightSidebarUpsellEnabled = useSelector((state) =>
    selectExperiment(state, NEW_RIGHTSIDEBAR_UPSELL_ENABLED),
  );
  const { getLocalizedText, location } = useContext(
    LocationAndLocalizationContext,
  );
  const shouldRender = useCallback(
    () => !!(!isUserSubscribed && isSubscriptionEnabled),
    [isUserSubscribed, isSubscriptionEnabled],
  );
  useEffect(() => {
    if (shouldRender()) {
      const eventLabel = getSubscribeEventLabel(labels.sidebarUpsell, location);
      dispatch(
        logSubscribeActivity(subscribe.actions.show, eventLabel, guideId),
      );
    }
  }, [guideId, location, dispatch, shouldRender]);
  if (!shouldRender()) {
    return null;
  }
  const eventLabel = getSubscribeEventLabel(labels.sidebarUpsell, location);
  let titleKey = isExpiredWebSubscriber
    ? UPSELL_UPGRADE_NOW
    : UPSELL_SIDEBAR_HEADLINE;
  const buttonKey = isExpiredWebSubscriber
    ? UPSELL_GO_PREMIUM
    : UPSELL_START_YOUR_FREE_TRIAL;

  if (newRightSidebarUpsellEnabled) {
    svgProps.fill = cssVariables['--premium-gold'];
    titleKey = RIGHTUPSELL_TITLE_DISCOUNT;
  }

  const title = getLocalizedText(titleKey).replace(
    LINE_BREAK_INSTRUCTION,
    LINE_BREAK_INTERPOLATE,
  );

  return (
    <div
      className={classNames(css.sidebarWrapper, {
        [css.isProfile]: isProfile,
        [css.newDesign]: newRightSidebarUpsellEnabled,
      })}
      id={dataTestIdPrefix}
      data-testid={dataTestIdPrefix}
    >
      {newRightSidebarUpsellEnabled && (
        <div className={css.newUpsellLogosContainer} />
      )}
      <h1
        id={`${dataTestIdPrefix}Headline`}
        data-testid={`${dataTestIdPrefix}Headline`}
        className={classNames(css.title, {
          [css.newDesign]: newRightSidebarUpsellEnabled,
        })}
      >
        {interpolateComponents({
          mixedString: title,
          components: {
            linebreak: <br />,
          },
        })}
      </h1>
      {!newRightSidebarUpsellEnabled && (
        <table
          className={classNames(css.list, {
            [css.newDesign]: newRightSidebarUpsellEnabled,
          })}
          id={`${dataTestIdPrefix}FeatureList`}
          data-testid={`${dataTestIdPrefix}FeatureList`}
        >
          <tbody>
            <tr
              id={`${dataTestIdPrefix}FeatureListCommercialFreeSports`}
              data-testid={`${dataTestIdPrefix}FeatureListCommercialFreeSports`}
            >
              <td>
                <Checkmark {...svgProps} />
              </td>
              <td>{getLocalizedText(UPSELL_COMMERCIAL_FREE_SPORTS)}</td>
            </tr>
            <tr
              id={`${dataTestIdPrefix}FeatureListCommercialFreeNews`}
              data-testid={`${dataTestIdPrefix}FeatureListCommercialFreeNews`}
            >
              <td>
                <Checkmark {...svgProps} />
              </td>
              <td>{getLocalizedText(UPSELL_COMMERCIAL_FREE_NEWS)}</td>
            </tr>
            {isCommercialFreeMusicEnabled && (
              <tr
                id={`${dataTestIdPrefix}FeatureListCommercialFreeMusic`}
                data-testid={`${dataTestIdPrefix}FeatureListCommercialFreeMusic`}
              >
                <td>
                  <Checkmark {...svgProps} />
                </td>
                <td>{getLocalizedText(UPSELL_COMMERCIAL_FREE_MUSIC)}</td>
              </tr>
            )}
            <tr
              id={`${dataTestIdPrefix}FeatureListNoDisplayAds`}
              data-testid={`${dataTestIdPrefix}FeatureListNoDisplayAds`}
            >
              <td>
                <Checkmark {...svgProps} />
              </td>
              <td>{getLocalizedText(UPSELL_LESS_ADS)}</td>
            </tr>
          </tbody>
        </table>
      )}
      <Link
        className={classNames(css.buttonLinkWrapper, {
          [css.newDesign]: newRightSidebarUpsellEnabled,
        })}
        data-testid="sidebarStartTrialLink"
        to={createSubscribeLink({
          source: eventLabel,
          guideId,
          location,
        })}
      >
        <PillButton
          className={classNames(css.button, {
            [css.newDesign]: newRightSidebarUpsellEnabled,
          })}
          id={`${dataTestIdPrefix}StartTrialButton`}
          dataTestId={`${dataTestIdPrefix}StartTrialButton`}
          onClick={() =>
            dispatch(
              logSubscribeActivity(subscribe.actions.tap, eventLabel, guideId),
            )
          }
          label={getLocalizedText(buttonKey)}
        />
      </Link>
    </div>
  );
}

UpsellRightSidebar.propTypes = {
  guideId: PropTypes.string,
  isProfile: PropTypes.bool,
};

export default UpsellRightSidebar;
